import React from 'react';
import { useLanguage } from '@/context/LanguageContext';
import { Package } from 'lucide-react';

const ProductsSection = () => {
  const { t } = useLanguage();

  return (
    <section id="products" className="section-spacing" data-testid="products-section">
      <div className="container-custom">
        <h2 
          className="text-3xl md:text-5xl font-bold text-[#0047AB] text-center mb-4"
          data-testid="products-title"
        >
          {t('productsTitle')}
        </h2>
        <div className="section-divider mb-12"></div>

        {/* TCM Detergent Powder */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12" data-testid="tcm-powder-card">
          <div className="flex items-center justify-center bg-slate-50">
            <img 
              src="https://customer-assets.emergentagent.com/job_gupta-grih-udyog/artifacts/z48t12rl_10%20T.C.M.%C2%AE%20Detergent%20Powder%20varations.png" 
              alt="T.C.M.® Detergent Powder"
              className="w-full h-auto max-h-[500px] object-contain"
            />
          </div>
          <div className="flex flex-col justify-center">
            <h3 className="text-2xl md:text-3xl font-bold text-slate-800 mb-4">T.C.M.® Detergent Powder</h3>
            <p className="text-slate-600 leading-relaxed mb-6">{t('tcmPowderDesc')}</p>
            <div className="bg-slate-50 p-4">
              <p className="font-semibold text-slate-800 mb-2">{t('availableSizes')}:</p>
              <div className="flex flex-wrap gap-2">
                {['5 kg', '3 kg', '1 kg', '500 g', '100 g'].map((size) => (
                  <span key={size} className="px-3 py-1 bg-[#0047AB] text-white text-sm font-medium">{size}</span>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* TCM Detergent Soap */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12" data-testid="tcm-soap-card">
          <div className="flex flex-col justify-center order-2 lg:order-1">
            <h3 className="text-2xl md:text-3xl font-bold text-slate-800 mb-4">T.C.M.® Detergent Soap</h3>
            <p className="text-slate-600 leading-relaxed mb-6">{t('tcmSoapDesc')}</p>
            <div className="bg-slate-50 p-4">
              <p className="font-semibold text-slate-800 mb-2">{t('available')}:</p>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <span className="font-medium">200 g</span>
                  <span className="text-slate-500">({t('greenVariant')} & {t('blueVariant')})</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-medium">100 g</span>
                  <span className="text-slate-500">({t('greenVariant')} & {t('blueVariant')})</span>
                </div>
              </div>
            </div>
          </div>
          <div className="flex items-center justify-center bg-slate-50 order-1 lg:order-2">
            <img 
              src="https://customer-assets.emergentagent.com/job_gupta-grih-udyog/artifacts/2d0m31v9_11%20T.C.M.%C2%AE%20Detergent%20Soap%20varations.png" 
              alt="T.C.M.® Detergent Soap"
              className="w-full h-auto max-h-[500px] object-contain"
            />
          </div>
        </div>

        {/* Jabaj Detergent Soap */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12" data-testid="jabaj-soap-card">
          <div className="flex items-center justify-center bg-slate-50">
            <img 
              src="https://customer-assets.emergentagent.com/job_gupta-grih-udyog/artifacts/bpolp5eq_12%20Jabaj%C2%AE%20Detergent%20Soap%20varation.png" 
              alt="Jabaj® Detergent Soap"
              className="w-full h-auto max-h-[500px] object-contain"
            />
          </div>
          <div className="flex flex-col justify-center">
            <h3 className="text-2xl md:text-3xl font-bold text-slate-800 mb-4">Jabaj® Detergent Soap</h3>
            <p className="text-slate-600 leading-relaxed mb-6">{t('jabajSoapDesc')}</p>
            <div className="bg-slate-50 p-4">
              <p className="font-semibold text-slate-800 mb-2">{t('available')}:</p>
              <div className="flex flex-wrap gap-2">
                {['250 g', '125 g'].map((size) => (
                  <span key={size} className="px-3 py-1 bg-[#0047AB] text-white text-sm font-medium">{size}</span>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* T.C.M. Bartan Bar */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12" data-testid="bartan-bar-card">
          <div className="flex flex-col justify-center order-2 lg:order-1">
            <h3 className="text-2xl md:text-3xl font-bold text-slate-800 mb-4">T.C.M.® Bartan Bar</h3>
            <p className="text-slate-600 leading-relaxed mb-4">{t('bartanBarIntro')}</p>
            <ul className="space-y-2 text-slate-600">
              <li className="flex items-start gap-2">
                <span className="text-green-600 font-bold">✔</span>
                <span><strong>Powerful Degreasing Formula</strong> – Cuts through oil and stubborn stains instantly</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-600 font-bold">✔</span>
                <span><strong>Anti-Bacterial Protection</strong> – Keeps your utensils hygienic and safe</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-600 font-bold">✔</span>
                <span><strong>Lemon Fresh Fragrance</strong> – Leaves a refreshing clean scent</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-600 font-bold">✔</span>
                <span><strong>Long-Lasting Bar</strong> – More usage, better value for money</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-600 font-bold">✔</span>
                <span><strong>Perfect for All Utensils</strong> – Steel, aluminum, and everyday cookware</span>
              </li>
            </ul>
          </div>
          <div className="flex items-center justify-center bg-slate-50 order-1 lg:order-2">
            <img 
              src="https://customer-assets.emergentagent.com/job_gupta-grih-udyog/artifacts/g8dlzj3f_file_000000007a187208875cc36ed46623f7.png" 
              alt="T.C.M.® Bartan Bar"
              className="w-full h-auto max-h-[500px] object-contain"
            />
          </div>
        </div>

        {/* Oil Based Laundry Soap */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12" data-testid="oil-soap-card">
          <div className="flex flex-col justify-center order-2 lg:order-1">
            <h3 className="text-2xl md:text-3xl font-bold text-slate-800 mb-4">{t('oilBasedSoap')}</h3>
            <p className="text-slate-600 leading-relaxed">{t('oilBasedDesc')}</p>
          </div>
          <div className="flex items-center justify-center bg-slate-50 order-1 lg:order-2">
            <img 
              src="https://customer-assets.emergentagent.com/job_gupta-grih-udyog/artifacts/t5whbfku_13%20Oil%20Based%20Laundry%20Soap.png" 
              alt="Oil Based Laundry Soap"
              className="w-full h-auto max-h-[500px] object-contain"
            />
          </div>
        </div>

        {/* Liquid Cleaning Products */}
        <div className="mt-16" data-testid="liquid-products-section">
          <h3 className="text-2xl md:text-3xl font-bold text-[#0047AB] text-center mb-8">{t('liquidTitle')}</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Hand Wash */}
            <div className="card-product p-6 product-card" data-testid="liquid-product-0">
              <div className="w-full aspect-square bg-slate-50 mb-4 flex items-center justify-center overflow-hidden">
                <img 
                  src="https://customer-assets.emergentagent.com/job_gupta-grih-udyog/artifacts/1lagw7up_14.png" 
                  alt="Hand Wash"
                  className="w-full h-full object-cover"
                />
              </div>
              <h4 className="font-bold text-slate-800 mb-2">{t('handWash')}</h4>
              <p className="text-sm text-slate-600">{t('handWashDesc')}</p>
            </div>

            {/* Toilet Cleaner */}
            <div className="card-product p-6 product-card" data-testid="liquid-product-1">
              <div className="w-full aspect-square bg-slate-50 mb-4 flex items-center justify-center overflow-hidden">
                <img 
                  src="https://customer-assets.emergentagent.com/job_gupta-grih-udyog/artifacts/dqgb5n77_15.png" 
                  alt="Toilet Cleaner"
                  className="w-full h-full object-cover"
                />
              </div>
              <h4 className="font-bold text-slate-800 mb-2">{t('toiletCleaner')}</h4>
              <p className="text-sm text-slate-600">{t('toiletCleanerDesc')}</p>
            </div>

            {/* White Phenyl */}
            <div className="card-product p-6 product-card" data-testid="liquid-product-2">
              <div className="w-full aspect-square bg-slate-50 mb-4 flex items-center justify-center overflow-hidden">
                <img 
                  src="https://customer-assets.emergentagent.com/job_gupta-grih-udyog/artifacts/hbs00a7b_16.png" 
                  alt="White Phenyl"
                  className="w-full h-full object-cover"
                />
              </div>
              <h4 className="font-bold text-slate-800 mb-2">{t('whitePhenyl')}</h4>
              <p className="text-sm text-slate-600">{t('whitePhenylDesc')}</p>
            </div>

            {/* Black Phenyl */}
            <div className="card-product p-6 product-card" data-testid="liquid-product-3">
              <div className="w-full aspect-square bg-slate-50 mb-4 flex items-center justify-center overflow-hidden">
                <img 
                  src="https://customer-assets.emergentagent.com/job_gupta-grih-udyog/artifacts/fpfwsln7_17.png" 
                  alt="Black Phenyl"
                  className="w-full h-full object-cover"
                />
              </div>
              <h4 className="font-bold text-slate-800 mb-2">{t('blackPhenyl')}</h4>
              <p className="text-sm text-slate-600">{t('blackPhenylDesc')}</p>
            </div>

            {/* Floor Cleaner */}
            <div className="card-product p-6 product-card" data-testid="liquid-product-4">
              <div className="w-full aspect-square bg-slate-50 mb-4 flex items-center justify-center overflow-hidden">
                <img 
                  src="https://customer-assets.emergentagent.com/job_gupta-grih-udyog/artifacts/bd8nvo15_18.png" 
                  alt="Floor Cleaner"
                  className="w-full h-full object-cover"
                />
              </div>
              <h4 className="font-bold text-slate-800 mb-2">{t('floorCleaner')}</h4>
              <p className="text-sm text-slate-600">{t('floorCleanerDesc')}</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProductsSection;
